<template>
  <q-card
    v-if="isValidAppointment"
    v-ripple="canClickAppointment"
    :data-e2e="`card-appointment-${appointmentStatus}`"
    :class="{'cursor-pointer': canClickAppointment}"
    @click="onClickAppointment"
  >
    <q-card-section
      horizontal
      class="row col-12"
    >
      <div class=" col-xs-4">
        <div
          class="row full-width no-select"
        >
          <div
            :class="{
              'bg-secondary': appointmentType !== 'unavailability',
              'bg-dark-variant': appointmentType === 'unavailability',
            }"
            class="row col-12 text-white justify-center text-bold text-overline
             text-uppercase items-center q-py-none"
          >
            {{ weekdayFormatted }}
          </div>
          <div
            class="row col-12 justify-center items-end q-py-none calendar-day"
          >
            <div class="text-h6 text-grey-7 q-pr-xs">
              {{ monthFormatted }}
            </div>
            <div class="text-h3 text-dark">
              {{ getDate(scheduledStartDate) }}
            </div>
          </div>
          <div
            :class="{
              'text-strike': isCancelled || isDeleted || isIntegrationCompleted,
              'text-negative': isCancelled || isDeleted,
              'text-positive': isIntegrationCompleted
            }"
            class="row col-12 q-py-none text-caption text-dark-variant bg-grey-2
             justify-center items-center text-center"
          >
            <div class="col-12">
              {{ timeFormatted }}
            </div>
            <div
              v-if="hasUser"
              class="col-12"
            >
              {{ appointmentUser }}
            </div>
            <q-separator
              v-if="showAppointmentNotes"
              style="width: 100%"
            />
            <appointment-notes
              v-if="showAppointmentNotes"
              :notes="appointment.notes"
            />
          </div>
        </div>
      </div>
      <q-card-section
        class="col q-ma-none q-pa-none column"
      >
        <q-card-section
          class="row col-auto items-top q-pt-sm q-pb-none ellipsis card-body"
        >
          <div
            v-if="hasClient && (!!clientPhoto)"
            class="row col-auto"
          >
            <q-avatar
              size="46px"
              class="q-mr-md"
            >
              <q-img :src="clientPhoto" />
            </q-avatar>
          </div>
          <div class="row col ">
            <div
              class="col-12 text-subtitle2"
            >
              {{ sessionName }}
            </div>
            <div
              v-if="hasClient"
              class="col-12"
            >
              {{ clientName }}
            </div>
            <div
              v-if="placeOfServiceLabel"
              class="text-caption text-grey-6 col-12"
            >
              <div class="ellipsis q-pr-xs">
                <q-icon
                  name="location_on"
                  size="xs"
                />
                {{ placeOfServiceLabel }}
              </div>
            </div>
          </div>
          <q-space />
          <div class="col-auto">
            <q-btn
              v-if="userCanSeeMenu"
              size="sm"
              flat
              round
              icon="more_vert"
              @click.stop
            >
              <q-menu>
                <q-list
                  class="text-dark"
                  style="min-width: 100px"
                >
                  <q-item
                    v-if="canAddManualSessionEntry"
                    v-close-popup
                    clickable
                    @click="onAddManualSessionEntry"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="add"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('appointments.menu.textAddManualSessionEntry') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canLinkToExistingActivity"
                    v-close-popup
                    clickable
                    @click="onLinkToExistingActivity"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="edit"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('appointments.menu.textLinkToActivity') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canAddPastStaffActivity"
                    v-close-popup
                    clickable
                    @click="onAddPastStaffActivity"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="edit"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('appointments.menu.addLinkNewActivity') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canRequestCancellation"
                    v-close-popup
                    clickable
                    @click="onRequestCancellation"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="event_busy"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('appointments.menu.textRequestCancellation') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="onCopySupportInfo"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="content_copy"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('common.copySupportInfo') }}
                    </q-item-section>
                  </q-item>
                  <q-separator />
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </q-card-section>
        <q-card-section
          class="row col justify-end items-center q-pt-none q-pb-sm"
        >
          <div class="col-12 full-height row items-end">
            <div class="col-12 row items-center">
              <div
                class="col"
              >
                <template
                  v-if="isRendered"
                >
                  <q-icon
                    name="verified"
                    color="positive"
                    size="sm"
                    class="q-mr-sm"
                  />
                  <span :class="{'hidden':$q.screen.lt.sm}">{{ $t('appointments.status.rendered') }}</span>
                </template>
                <template
                  v-else-if="isCompleted"
                >
                  <q-icon
                    name="done"
                    color="positive"
                    size="sm"
                    class="q-mr-sm"
                  />
                  <span :class="{'hidden':$q.screen.lt.sm}">{{ $t('appointments.status.completed') }}</span>
                </template>
                <template
                  v-else-if="isLinkedActivityStatus"
                >
                  <q-icon
                    :name="getAppointmentStatusIcon(appointment)"
                    :color="getAppointmentStatusColor(appointment)"
                    size="sm"
                    class="q-mr-sm"
                  />
                  <span :class="{'hidden':$q.screen.lt.sm}">{{ getAppointmentStatusLabel(appointment) }}</span>
                </template>
                <template
                  v-else-if="isIntegrationCompleted"
                >
                  <q-icon
                    name="done"
                    color="positive"
                    size="sm"
                    class="q-mr-sm"
                  />
                  <span :class="{'hidden':$q.screen.lt.sm}">
                    {{ $t('appointments.status.integrationCompleted', {integration: currentIntegration}) }}
                  </span>
                </template>
                <template
                  v-if="isInProgress"
                >
                  <q-spinner-dots
                    color="primary"
                    size="2em"
                    class="q-mr-md"
                  />
                  <span :class="{'hidden':$q.screen.lt.sm}">{{ $t('appointments.status.in-progress') }}</span>
                </template>
                <template
                  v-else-if="isCancelled"
                >
                  <div class="items-center flex">
                    <q-icon
                      name="cancel"
                      color="negative"
                      size="sm"
                      class="q-mr-sm"
                    />
                    <span
                      class="column"
                      :class="{'hidden':$q.screen.lt.sm}"
                    >
                      <span>
                        {{ $t('appointments.status.cancelled') }}
                      </span>
                      <span v-if="isCancellationApproved">
                        {{ `${$t('appointments.cancellationRequest.reasonLabel')}: ${cancellationReason}` }}
                      </span>
                    </span>
                  </div>
                </template>
                <template
                  v-else-if="isCancellationRequested"
                >
                  <div class="items-center flex">
                    <q-icon
                      name="error"
                      color="warning"
                      size="sm"
                      class="q-mr-sm"
                    />
                    <span
                      class="column"
                      :class="{'hidden':$q.screen.lt.sm}"
                    >
                      <span>
                        {{ $t('appointments.status.cancellationRequested') }}
                      </span>
                      <span>
                        {{ `${$t('appointments.cancellationRequest.reasonLabel')}: ${cancellationReason}` }}
                      </span>
                    </span>
                  </div>
                </template>
                <template
                  v-else-if="isDeleted"
                >
                  <q-icon
                    name="cancel"
                    color="negative"
                    size="sm"
                    class="q-mr-sm"
                  />
                  <span :class="{'hidden':$q.screen.lt.sm}">{{ $t('appointments.status.deleted') }}</span>
                </template>
              </div>
              <div
                v-if="hasActionButton"
                class="col-auto"
              >
                <universal-tooltip
                  :show-tooltip="disableActionButton"
                  :text="tooltipText"
                >
                  <q-btn
                    :label="actionButtonLabel"
                    data-e2e="button-appointment-action"
                    flat
                    :disable="disableActionButton"
                    :loading="isUpdating"
                    :icon="actionButtonIcon"
                    color="positive"
                  />
                </universal-tooltip>
              </div>
            </div>
          </div>
        </q-card-section>
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
<script>
import { callFirebaseFunction } from 'boot/firebase';
import AppointmentNotes from 'components/Appointments/AppointmentNotes';
import CancelAppointmentDialog from 'components/Appointments/CancelAppointmentDialog.vue';
import ChangeAppointmentSessionActivity from 'components/SessionActivities/Modals/ChangeAppointmentSessionActivity';
import EditSessionActivityDialog from 'components/SessionActivities/Modals/EditSessionActivityDialog';
import StartSessionActivityDialog from 'components/SessionActivities/Modals/StartSessionActivityDialog';
import SessionActivityDialog from 'components/Sessions/Modals/SessionActivityDialog';
import SessionCardDialog from 'components/Sessions/Modals/SessionCardDialog';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { find, get, pick } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { ACTION_NOT_AVAILABLE_REASONS, useNotifications } from 'src/composables/notifications';
import { useSessionActivities } from 'src/composables/sessionActivities';
import { useTime } from 'src/composables/time';
import { canSendCancellationRequests } from 'src/config/integrations';
import { CANCELLATION_REQUEST_STATUSES, INTEGRATION_NAMES } from 'src/constants/integrations';
import {
  ACTIVITY_STATUSES_FOR_APPOINTMENTS,
  SESSION_ACTIVITY_TYPES,
  SESSION_ACTIVITY_STATUSES,
} from 'src/constants/sessionActivities';
import AppointmentsDB from 'src/fb/appointments-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import UserNotificationsDB from 'src/fb/userNotifications-db';
import AppointmentsMixin from 'src/mixins/AppointmentsMixin';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import { sessionAnalytics } from 'src/services/analytics';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { geolocation as geolocationStore } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { sessions } from 'src/store/sessions';
import { copySupportInfoToClipboard, notifyErrorWithDetails, userRoles } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'AppointmentCard',

  components: { AppointmentNotes, UniversalTooltip },

  mixins: [AppointmentsMixin, Auth, Interactions, SessionActivitiesMixin],

  props: {
    appointment: {
      type: Object,
      required: true,
    },
    enableCustomActionButton: {
      type: Boolean,
      default: false,
    },
    customActionButton: {
      type: Object,
      default: null,
    },
    checkPendingActions: {
      type: Boolean,
      default: false,
    },
    fakeRole: {
      type: [Object, null],
      default: null,
    },
  },

  emits: ['click', 'refresh'],

  setup() {
    const { goToNotification, isNotificationActionAvailable } = useNotifications();
    const { checkUserCanStartSessionActivity } = useSessionActivities();
    const { getLocaleDateString, getLocaleTimeString, getDate, addToDate, isSameDate } = useTime();

    return {
      goToNotification,
      isNotificationActionAvailable,
      checkUserCanStartSessionActivity,
      getLocaleDateString,
      getLocaleTimeString,
      getDate,
      addToDate,
      isSameDate,
    };
  },

  data() {
    return {
      isUpdating: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'connectedClinic', 'isParent', 'hasTrainerCredentials',
      'activityTypesSettings', 'cancellationReasons', 'isManagerOrAdministrator', 'integrationSettings']),
    ...mapState(network, ['offlineMode']),
    ...mapState(clients, ['clients']),

    canClickAppointment() {
      return (this.hasActionButton && !this.disableActionButton && !this.isUpdating);
    },

    isCurrentClient() {
      return get(this.appointment, 'client') === get(this.currentClient, 'id');
    },

    hasClient() {
      return !!get(this.appointment, 'client');
    },

    hasUser() {
      return get(this.appointment, 'user', null) !== null;
    },

    userCanSeeMenu() {
      return (this.canAddManualSessionEntry || this.canLinkToExistingActivity || this.canRequestCancellation)
        && this.appointmentType !== 'unavailability'
        && !this.isCancellationRequested;
    },

    canAddManualSessionEntry() {
      return (['scheduled', 'completed'].includes(this.appointment.status))
        && this.appointment.client
        && !this.appointment.sessionActivity
        && !this.appointment.isRecurring
        && this.appointment.session
        && this.appointment.user === this.userDetails.userId
        && this.userHasPrivilege('sessions/enterManualSessions');
    },

    canLinkToExistingActivity() {
      return (['scheduled', 'completed'].includes(this.appointment.status))
        && !this.appointment.sessionActivity
        && !this.isParent
        && !this.appointment.isRecurring
        && !this.isCancellationRequested;
    },

    appointmentUser() {
      return get(this.appointment, 'userName', null);
    },

    isValidAppointment() {
      return (get(this.appointment, 'scheduledStartTimestamp') instanceof Timestamp);
    },

    placeOfServiceLabel() {
      return this.formatPlaceOfServiceLabel(this.appointment, this.client);
    },

    scheduledStartDate() {
      return this.appointment.scheduledStartTimestamp.toDate();
    },

    scheduledEndDate() {
      return this.addToDate(this.scheduledStartDate, { seconds: this.appointment.duration });
    },

    timeFormatted() {
      const options = { hour: 'numeric', minute: '2-digit' };
      return `${this.getLocaleTimeString(
        this.scheduledStartDate,
        { options },
      )} ${this.$t('common.to')} ${this.getLocaleTimeString(this.scheduledEndDate, { options })}`;
    },

    weekdayFormatted() {
      return this.getLocaleDateString(this.scheduledStartDate, { options: { weekday: 'long' } });
    },

    monthFormatted() {
      return this.getLocaleDateString(this.scheduledStartDate, { options: { month: 'short' } });
    },

    clientPhoto() {
      return get(this.client, 'photo', null);
    },

    clientName() {
      return get(this.client, 'clientName', null);
    },

    sessionName() {
      const sessionName = get(this.appointment, 'sessionName', null);
      if (sessionName) {
        return sessionName;
      }
      if (!get(this.appointment, 'client')) {
        return this.$t('appointments.staffOnlySession');
      }
      return this.$t('appointments.clientSession');
    },

    client() {
      return find(this.clients, { id: get(this.appointment, 'client') });
    },

    isRendered() {
      return get(this.appointment, 'isRendered', false) === true;
    },

    isLinkedActivityStatus() {
      // Activity status is available for clinics without integrations, or with integrations that allow scheduling
      if (this.connectedClinic?.integration && !this.integrationSettings?.allowScheduling) {
        return false;
      }

      return Object.values(ACTIVITY_STATUSES_FOR_APPOINTMENTS)
        .includes(this.appointmentStatus);
    },

    isCompleted() {
      return this.appointmentStatus === 'completed';
    },

    isIntegrationCompleted() {
      return this.appointmentStatus === 'integration-completed';
    },

    currentIntegration() {
      return this.getIntegrationName(this.connectedClinic?.integration);
    },

    isInProgress() {
      return this.appointmentStatus === 'in-progress';
    },

    // @todo: rename 'cancelled' into 'canceled'; migration for existing appointments required

    isCancelled() {
      return this.appointmentStatus === 'cancelled';
    },

    isCancellationRequested() {
      return this.appointment?.cancellationRequest?.status === CANCELLATION_REQUEST_STATUSES.PENDING;
    },

    isCancellationApproved() {
      return this.appointment?.cancellationRequest?.status === CANCELLATION_REQUEST_STATUSES.APPROVED;
    },

    cancellationReason() {
      if (this.isCancellationRequested || this.isCancellationApproved) {
        const type = this.$tm('cancellationTypes')?.[this.appointment?.cancellationRequest?.type] || '';
        const reason = this.cancellationReasons.find(({ id }) => {
          return id === this.appointment?.cancellationRequest?.reason;
        })?.cancellationReason || '';
        return `${type} ${reason}`;
      }
      return '';
    },

    isDeleted() {
      return this.appointmentStatus === 'deleted';
    },

    isAlohaIntegration() {
      return this.connectedClinic?.integration === 'aloha';
    },

    // It's possible to have appointment from Aloha with unlinked client
    isDisabledAlohaAppointment() {
      return this.actionButtonType === 'goto-client'
      && this.isAlohaIntegration
      && !(this.appointment?.integrationClientParRef && this.appointment?.client);
    },

    // Appointments pushed by API v2 integrations can arrive for a client
    // that doesn't exist or isn't linked in HiRasmus yet, saved with `client: null`.
    hasUnlinkedIntegrationClient() {
      return this.appointment?.client === null
        && !!this.appointment?.integrationData?.references?.clientId;
    },

    isStaffOnlyAppointment() {
      if (this.isAlohaIntegration) {
        return this.appointment?.integrationClientParRef === null;
      }

      return this.appointment?.client === null;
    },

    appointmentStatus() {
      return this.getStatusBasedOnLinkedActivity(this.appointment);
    },

    appointmentType() {
      return get(this.appointment, 'type', 'appointment');
    },

    actionButtonType() {
      if (this.enableCustomActionButton) {
        return 'custom';
      }

      if (this.appointmentType === 'unavailability') {
        return null;
      }

      if (this.isCancellationRequested) {
        return null;
      }

      if (this.isStaffOnlyAppointment) {
        switch (this.appointmentStatus) {
          case 'scheduled':
            return 'activity-start';
          case 'in-progress':
            return 'activity-finish';
          default:
            if (get(this.appointment, 'sessionActivity', null) !== null) {
              return 'activity-view';
            }
        }
      } else {
        if (this.isLinkedActivityStatus) {
          return 'session-view';
        }

        switch (this.appointmentStatus) {
          case 'completed':
          case 'integration-completed':
            if (get(this.appointment, 'sessionActivity', null) !== null) {
              return 'session-view';
            }
            break;
          case 'scheduled': {
            if ((get(this.appointment, 'session', null) !== null)
              && (get(this.appointment, 'user', null) === this.userDetails.userId)) {
              if (get(this.appointment, 'groupSessionRefId', null) !== null) {
                return 'group-session-start';
              }
              return 'session-start';
            }
            return 'goto-client';
          }
          case 'cancelled':
          case 'deleted': {
            return null;
          }
          case 'in-progress': {
            if ((get(this.appointment, 'session', null) !== null)
              && (get(this.appointment, 'user', null) === this.userDetails.userId)) {
              return 'session-join';
            }
            return null;
          }
          default:
            return 'goto-client';
        }
      }
      return null;
    }, // actionButtonType()

    hasActionButton() {
      return this.actionButtonType !== null;
    },

    actionButtonLabel() {
      switch (this.actionButtonType) {
        case 'custom':
          return get(this.customActionButton, 'label', null);
        case 'activity-view':
          return this.$t('appointments.buttonViewSessionActivity');
        case 'activity-start':
          return this.$t('appointments.buttonStartSessionActivity');
        case 'activity-finish':
          return this.$t('appointments.buttonFinishSessionActivity');
        case 'session-view':
          return this.$t('appointments.buttonViewSession');
        case 'session-start':
          return this.$t('appointments.buttonStartSession');
        case 'session-join':
          return this.$t('appointments.buttonJoinSession');
        case 'goto-client':
          return this.$t('appointments.buttonGoToClient');
        case 'group-session-start':
          return this.$t('appointments.buttonStartGroupSession');
        default:
          return '';
      }
    }, // actionButtonLabel()

    disableActionButton() {
      const isSessionStart = ['session-start', 'group-session-start', 'session-join'].includes(this.actionButtonType);
      const hasStartSessionsPrivilege = this.userHasPrivilege('sessions/startSessions');

      if (isSessionStart && !hasStartSessionsPrivilege) {
        return true;
      }

      if (this.offlineMode && ['group-session-start'].includes(this.actionButtonType)) {
        // Disable action button if it's a group session and we are offline
        return true;
      }

      if (this.actionButtonType === 'activity-start' && this.hasUnlinkedIntegrationClient) {
        return true;
      }

      if (['activity-start', 'session-start'].includes(this.actionButtonType)) {
        // Disable action button if appointment is scheduled and scheduled start time is too far in the future or past
        return !this.isAppointmentAvailableToRun(
          this.scheduledStartDate,
          this.scheduledEndDate,
        );
      }

      return this.isDisabledAlohaAppointment;
    },

    actionButtonIcon() {
      switch (this.actionButtonType) {
        case 'custom':
          return get(this.customActionButton, 'icon', null);
        case 'activity-view':
        case 'activity-start':
        case 'activity-finish': {
          const activityTypeIcon = get(this.activityTypesSettings, [this.appointment.activityType, 'icon'], null);
          if (activityTypeIcon) {
            return activityTypeIcon;
          }
          return this.actionButtonType === 'scheduled' ? 'event-available' : 'event';
        }
        case 'session-join':
          return 'screen_share';
        case 'session-view':
        case 'session-start':
        case 'group-session-start':
        case 'goto-client':
        default:
          return 'event';
      }
    },

    tooltipText() {
      if (!this.disableActionButton) {
        return '';
      }

      if (this.hasUnlinkedIntegrationClient) {
        return this.$t('appointments.tooltipUnlinkedIntegrationClient');
      }

      if (this.isDisabledAlohaAppointment) {
        return this.$t('appointments.disabledAlohaAppointmentTooltip');
      }

      if (!this.isAppointmentAvailableToRun(this.scheduledStartDate, this.scheduledEndDate)) {
        return this.$t('appointments.tooltipAppointmentNotAvailable');
      }

      if (this.offlineMode && ['group-session-start'].includes(this.actionButtonType)) {
        return this.$t('sessions.sessionCard.disabledGroupOffline');
      }

      if (['session-start', 'group-session-start'].includes(this.actionButtonType)) {
        return this.$t('sessions.sessionCard.disabledStartSession');
      }

      if (this.actionButtonType === 'session-join') {
        return this.$t('sessions.sessionCard.disabledResumeSession');
      }

      return this.$t('sessions.sessionCard.disabledEnterSession');
    },

    /**
     * Verifies if appointment is not scheduled for a future date
     */
    hasApplicableAppointmentStartTime() {
      if (!(this.appointment?.scheduledStartTimestamp instanceof Timestamp)) {
        return false;
      }

      const startDate = this.appointment.scheduledStartTimestamp.toDate();
      const now = new Date();

      return startDate <= now || (startDate > now && this.isSameDate(startDate, now, 'day'));
    }, // hasApplicableAppointmentStartTime()

    showAppointmentNotes() {
      return this.appointment.notes && (this.appointment.isNotesVisible ?? true) && this.hasTrainerCredentials
        && this.fakeRole?.role !== userRoles.PARENT;
    }, // showAppointmentNotes()

    canRequestCancellation() {
      return canSendCancellationRequests(this.connectedClinic?.integration)
        && this.appointmentStatus === 'scheduled'
        && this.userHasPrivilege('sessionActivities/allowCancellationRequest')
        && !this.isCancellationRequested;
    },

    canAddPastStaffActivity() {
      return [INTEGRATION_NAMES.CAMBER, INTEGRATION_NAMES.LUMARY].includes(this.connectedClinic?.integration)
        && this.isStaffOnlyAppointment
        && !this.appointment.sessionActivity
        && !this.isAppointmentAvailableToRun(this.scheduledStartDate, this.scheduledEndDate)
        && this.scheduledEndDate < new Date();
    },

  },

  methods: {
    ...mapActions(auth, ['changeCurrentClientById']),
    ...mapActions(geolocationStore, ['refreshGeolocation', 'executeGeolocationCallback']),
    ...mapActions(sessions, ['duplicateSession']),

    async onClickAppointment() {
      if (!this.canClickAppointment) {
        return;
      }
      this.$emit('click');
      switch (this.actionButtonType) {
        case 'custom':
          return;
        case 'activity-view':
          return this.viewActivity();
        case 'activity-start':
          return this.startActivity();
        case 'activity-finish':
          return this.finishActivity();
        case 'session-view':
          return this.viewSession();
        case 'session-start':
          return this.startSession();
        case 'session-join':
          return this.joinSession();
        case 'goto-client':
          return this.goToClient();
        case 'group-session-start':
          return this.startSession('Prepare Group Session');
        default:
          return 'event';
      }
    },

    onAddManualSessionEntry() {
      this.addManualSessionData();
    },

    onLinkToExistingActivity() {
      this.linkToExistingActivity();
    },

    async addManualSessionData() {
      if (!this.appointment.session) {
        return;
      }

      this.$q.loading.show();
      const sessionsDb = new SessionsDB();
      let sessionActivity;
      let session;

      try {
        session = await sessionsDb.read(this.appointment.session);

        if (session.client !== this.appointment.client) {
          this.$q.loading.hide();
          this.alertDialog(
            this.$t('appointments.titleClientMismatch'),
            this.isManagerOrAdministrator
              ? this.$t('appointments.textClientMismatchManager')
              : this.$t('appointments.textClientMismatch'),
          );
          return;
        }

        if (session.isDeleted === true) {
          this.$q.loading.hide();
          this.notifyNegative(this.$t('run.overview.notifySessionDeleted'));
          return;
        }

        const timestamp = {
          startTimestamp: Timestamp.fromDate(new Date(Date.now() - 60 * 60 * 1000)),
          endTimestamp: Timestamp.fromDate(new Date(Date.now())),
        };

        if (this.hasApplicableAppointmentStartTime) {
          const { duration, scheduledStartTimestamp } = this.appointment;
          timestamp.startTimestamp = scheduledStartTimestamp;
          timestamp.endTimestamp = Timestamp.fromMillis(scheduledStartTimestamp.toMillis() + (duration || 0) * 1000);
        }

        sessionActivity = {
          ...timestamp,
          status: 'finished',
          type: 'session',
          user: this.userDetails.userId,
          userName: this.userDetails.name,
          client: session.client,
          clinic: this.userDetails.clinic,
          notes: '',
          sessionDurationOffset: 0,
          sessionNoteTemplateId: session.sessionNoteTemplate,
          version: 2,
          isDeleted: false,
        };
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err, {
          context: {
            context_data: {
              appointmentId: this.appointment.id,
              sessionId: this.appointment.session,
            },
          },
        });
        return;
      } finally {
        this.$q.loading.hide();
      }

      // Show session activity timing dialog
      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity,
        title: this.$t('sessions.sessionCard.textAddManualSessionEntry'),
        lockUser: true,
        lockDate: this.hasApplicableAppointmentStartTime,
      }, async (sessionActivityChanges) => {
        if ((!(get(sessionActivityChanges, 'startTimestamp') instanceof Timestamp))
          || (!(get(sessionActivityChanges, 'endTimestamp') instanceof Timestamp))) {
          return;
        }
        const newSessionActivity = {
          ...sessionActivity,
          ...sessionActivityChanges,
        };
        const duration = (
          newSessionActivity.endTimestamp.toDate()
          - newSessionActivity.startTimestamp.toDate()
        ) / 1000;
        newSessionActivity.sessionDurationEnd = duration;

        // Duplicate and make session ready
        this.$q.loading.show();
        if (!this.isCurrentClient) {
          await this.changeCurrentClientById(this.appointment.client);
        }

        const newSessionId = await this.duplicateSession({
          sessionId: session.id,
          sessionProps: {
            shuffle: false,
            originalSessionId: session.id,
            currentProgram: null,
            runBy: newSessionActivity.user,
            runByName: newSessionActivity.userName,
            runTimestamp: newSessionActivity.startTimestamp,
            finishTimestamp: newSessionActivity.endTimestamp,
            supervisedBy: null,
            supervisedByName: null,
            duration,
            hasTimecodedData: true,
            status: 'done',
          },
        });

        await sessionsDb.logScheduledSessionHistory(session.id, newSessionId, 60);

        const sessionActivitiesDb = new SessionActivitiesDB();

        const baseSessionActivityData = {
          ...newSessionActivity,
          session: newSessionId,
          sessionName: session.sessionName || null,
          createdBy: this.userDetails.userId,
        };

        if (this.hasApplicableAppointmentStartTime) {
          const appointmentData = pick(
            this.appointment,
            [
              'integrationReference',
              'integrationClientParRef',
              'integrationStaffParRef',
              'integrationClientRef',
              'integrationStaffRef',
              'integrationAppointmentType',
              'integration',
              'integrationExternalUrl',
            ],
          );

          const { id } = await sessionActivitiesDb.create({
            ...baseSessionActivityData,
            appointment: this.appointment.id || null,
            appointmentData,
          });

          const appointmentsDb = new AppointmentsDB();
          await appointmentsDb.set({
            session: newSessionId,
            sessionActivity: id,
            status: 'completed',
          }, this.appointment.id);
        } else {
          await sessionActivitiesDb.create({
            ...baseSessionActivityData,
            appointment: null,
          });
        }

        // Redirect to history
        this.$router.push({
          name: 'History View - Edit',
          params: { sessionId: newSessionId },
        });
        this.$q.loading.hide();
      });
    }, // addManualSessionData()

    linkToExistingActivity() {
      if (this.appointment.sessionActivity) {
        // Already linked...
        return;
      }

      const startDate = this.scheduledStartDate;

      this.openComponentDialog(ChangeAppointmentSessionActivity, {
        sessionActivityId: this.appointment.sessionActivity || '',
        clientId: this.appointment.client || null,
        userId: this.appointment.user || null,
        startDate,
      }, async (newSessionActivityId) => {
        if (!newSessionActivityId) {
          return;
        }
        const appointmentData = pick(
          this.appointment,
          [
            'integrationReference',
            'integrationClientParRef',
            'integrationStaffParRef',
            'integrationClientRef',
            'integrationStaffRef',
            'integrationAppointmentType',
            'integration',
            'integrationExternalUrl',
          ],
        );

        // todo: wrap in transaction
        const sessionActivitiesDb = new SessionActivitiesDB();
        const sessionActivity = await sessionActivitiesDb.read(newSessionActivityId);
        await sessionActivitiesDb.set({
          appointment: this.appointment.id || null,
          appointmentData,
        }, newSessionActivityId);

        const appointmentsDb = new AppointmentsDB();
        await appointmentsDb.set({
          sessionActivity: newSessionActivityId,
          session: sessionActivity?.session || null,
          status: sessionActivity?.status === 'in-progress' ? 'in-progress' : 'completed',
        }, this.appointment.id);

        if (sessionActivity?.type) {
          sessionAnalytics.trackActivityLinkedToAppointmentManually({
            sessionActivityId: newSessionActivityId,
            sessionActivityType: sessionActivity.type,
            appointmentId: this.appointment.id,
          });
        }

        this.$emit('refresh');
      });
    }, // linkToExistingActivity()

    async startSession(sessionStartPath = 'Session Overview') {
      try {
        const sessionId = this.appointment.session;

        if (!sessionId) {
          return;
        }

        this.$q.loading.show();

        if (!await this.checkUserCanStartSessionActivity(this.appointment.client || null)) {
          return;
        }

        if (this.checkPendingActions) {
          const pendingActionNotification = await this.findPendingActionNotificationThatPreventsSessionStart();

          if (pendingActionNotification) {
            const { available, reason } = await this.isNotificationActionAvailable(pendingActionNotification);

            if (available) {
              this.confirmDialog(
                this.$t('sessions.sessionCard.completeActionNotificationBeforeStartingSession.title'),
                this.$t('sessions.sessionCard.completeActionNotificationBeforeStartingSession.message'),
                () => this.goToNotification(pendingActionNotification),
                {
                  ok: {
                    label: this.$t('sessions.sessionCard.completeActionNotificationBeforeStartingSession.button'),
                    flat: true,
                  },
                },
              );
              return;
            }

            const userNotificationsDb = new UserNotificationsDB();

            if (
              [ACTION_NOT_AVAILABLE_REASONS.RESOURCE_DELETED, ACTION_NOT_AVAILABLE_REASONS.UNKNOWN].includes(reason)
            ) {
              await userNotificationsDb.delete(pendingActionNotification.id);
            } else if (
              reason === ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED
              && !pendingActionNotification.actionCompleted
            ) {
              await userNotificationsDb.set({
                actionCompleted: true,
                actionCompletedTimestamp: serverTimestamp(),
                viewed: true,
              }, pendingActionNotification.id);
            }
          }
        }

        const sessionsDb = new SessionsDB();
        const session = await sessionsDb.read(sessionId);

        if (session.client !== this.appointment.client) {
          this.alertDialog(
            this.$t('appointments.titleClientMismatch'),
            this.isManagerOrAdministrator
              ? this.$t('appointments.textClientMismatchManager')
              : this.$t('appointments.textClientMismatch'),
          );
          return;
        }

        if (!this.isCurrentClient) {
          await this.changeCurrentClientById(this.appointment.client);
        }
        const query = {};
        if (!this.appointment.isRecurring) {
          query.appointment = this.appointment.id;
        }
        this.$router.push({
          name: sessionStartPath,
          params: { sessionId: this.appointment.session },
          query,
        });
      } catch (err) {
        this.notifyNegative(err.message);
        console.error(err);
        logEvent(err, {
          context: {
            context_data: {
              appointmentId: this.appointment.id,
              sessionId: this.appointment.session,
            },
          },
        });
      } finally {
        this.$q.loading.hide();
      }
    },

    async joinSession() {
      const sessionId = this.appointment.session;
      if (!sessionId) {
        return;
      }
      this.$q.loading.show();
      try {
        if (!this.isCurrentClient) {
          await this.changeCurrentClientById(this.appointment.client);
        }
        this.$router.push({
          name: 'session-data-collection',
          params: { sessionId: this.appointment.session },
        });
      } finally {
        this.$q.loading.hide();
      }
    },

    async goToClient() {
      if (this.isCurrentClient) {
        return this.$router.push({ name: 'Home' });
      }
      if (this.isStaffOnlyAppointment) {
        return;
      }
      this.$q.loading.show();
      try {
        await this.changeCurrentClientById(this.appointment.client);
        this.$router.push({ name: 'Home' });
      } finally {
        this.$q.loading.hide();
      }
    }, // goToClient()

    async startActivity() {
      this.isUpdating = true;
      if (!await this.checkUserCanStartSessionActivity(this.appointment.client || null)) {
        this.isUpdating = false;
        return;
      }

      this.refreshGeolocation(false);
      const startTimestamp = this.appointment.scheduledStartTimestamp || null;
      let endTimestamp = null;
      if (startTimestamp instanceof Timestamp) {
        const duration = this.appointment.duration || 60 * 60;
        endTimestamp = Timestamp.fromMillis(startTimestamp.toMillis() + duration * 1000);
      }
      const type = this.appointment.activityType || 'other';
      const sessionActivity = {
        startTimestamp,
        endTimestamp,
        status: 'in-progress',
        type,
        user: this.userDetails.userId,
        userName: this.userDetails.name,
        client: this.appointment.client || null,
        clinic: this.userDetails.clinic,
        notes: '',
        sessionDurationOffset: 0,
        sessionNoteTemplateId: this.appointment.sessionNoteTemplateId || null,

        appointment: this.appointment.id || null,
        session: null,
        sessionName: get(this.appointment, 'sessionName', null),
        appointmentData: pick(
          this.appointment,
          [
            'integrationReference',
            'integrationClientParRef',
            'integrationStaffParRef',
            'integrationClientRef',
            'integrationStaffRef',
            'integrationAppointmentType',
            'integration',
            'integrationExternalUrl',
          ],
        ),

        createdBy: this.userDetails.userId,

        version: 2,

        isDeleted: false,
      };

      this.isUpdating = false;
      // Show session activity timing dialog
      this.openComponentDialog(StartSessionActivityDialog, {
        sessionActivity,
        title: this.appointment.sessionName || this.$t('appointments.buttonStartSessionActivity'),
      }, async (newSessionActivity) => {
        this.isUpdating = true;
        const sessionActivitiesDb = new SessionActivitiesDB();

        try {
          const isAppointmentLinkedToActivities = await this.isAppointmentLinkedToActivities(
            this.appointment.id,
            this.userDetails.clinic,
          );

          if (isAppointmentLinkedToActivities) {
            this.notifyNegative(this.$t('appointments.textLinkedToActivity'));
            return;
          }

          const createdSessionActivity = await sessionActivitiesDb.create({
            ...sessionActivity,
            ...newSessionActivity,
            client: this.appointment.client || null,
            clinic: this.userDetails.clinic,
          });

          if (createdSessionActivity.id) {
            const appointmentsDb = new AppointmentsDB();
            await appointmentsDb.set({
              status: 'in-progress',
              sessionActivity: createdSessionActivity.id,
            }, this.appointment.id);
          }
          this.$emit('refresh');
        } catch (err) {
          this.notifyNegative(err.message);
        } finally {
          this.isUpdating = false;
        }
      });
    }, // startActivity()

    finishActivity() {
      if (!this.appointment.sessionActivity) {
        return;
      }
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t(
          'appointments.confirmFinishActivity',
        ),
        async () => {
          this.isUpdating = true;
          this.refreshGeolocation(false);

          const appointmentsDb = new AppointmentsDB();
          const sessionActivitiesDb = new SessionActivitiesDB();

          // @todo use firebase transaction to perform these update operations
          await Promise.all([
            appointmentsDb.update({
              id: this.appointment.id,
              status: 'completed',
            }),
            sessionActivitiesDb.update({
              id: this.appointment.sessionActivity,
              appointment: this.appointment.id,
              status: await this.applySessionActivityFinishedStatus(
                this.appointment.sessionActivity,
                'AppointmentCard.finishActivity',
              ),
              endTimestamp: Timestamp.now(),
            }),
          ]);

          await this.refreshLumaryAppointments(this.connectedClinic.integration, this.appointment);

          this.executeGeolocationCallback((geolocation) => {
            const sessionActivityId = this.appointment?.sessionActivity;
            if (!this || !geolocation || !sessionActivityId) {
              // The dialog was destroyed while waiting for the geolocation or the appointment has no session activity
              return;
            }

            sessionActivitiesDb.update({
              id: sessionActivityId,
              endGeolocation: geolocation,
            });
          });

          this.isUpdating = false;
          this.openComponentDialog(
            SessionActivityDialog,
            {
              sessionActivityId: this.appointment.sessionActivity,
              applyImmediately: true,
            },
            () => {
              this.$emit('refresh');
            },
            null,
            () => {
              this.$emit('refresh');
            },
          );
        },
      );
    }, // finishActivity()

    viewActivity() {
      const sessionActivityId = get(this.appointment, 'sessionActivity', null);

      if (!sessionActivityId) {
        return;
      }
      this.openComponentDialog(SessionActivityDialog, {
        sessionActivityId,
      }, () => {
        this.$emit('refresh');
      });
    },

    async viewSession() {
      const sessionId = get(this.appointment, 'session', null);
      if (!sessionId) {
        return;
      }
      const sessionActivityId = get(this.appointment, 'sessionActivity', null);
      try {
        this.$q.loading.show();
        const sessionsDb = new SessionsDB();
        const session = await sessionsDb.read(sessionId);
        if (get(session, 'isDeleted')) {
          this.$q.loading.hide();
          return;
        }

        const { allPrograms, client } = await this.loadSessionDialogData(session);

        this.openComponentDialog(SessionCardDialog, {
          allPrograms,
          session,
          client,
          ...(sessionActivityId ? { openSessionActivity: sessionActivityId } : {}),
        }, async () => {
          this.$emit('refresh');
        });
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.$q.loading.hide();
      }
    },

    onCopySupportInfo() {
      copySupportInfoToClipboard({
        'appointment id': this.appointment?.id,
        session: this.appointment?.session,
      });
    },

    async requestCancellation(cancellationReason, cancellationType, cancellationReasonLabel) {
      const cancellationRequest = {
        reason: cancellationReason,
        type: cancellationType,
        requestedBy: this.userDetails.userId,
        status: CANCELLATION_REQUEST_STATUSES.PENDING,
        message: null,
      };

      try {
        await callFirebaseFunction(
          'requestCancellation',
          {
            clinicId: this.userDetails.clinic,
            cancellationRequest,
            cancellationReasonLabel,
            appointmentId: this.appointment.id,
            requestedBy: {
              userId: this.userDetails.userId,
              partnerUserId: this.userDetails?.integrationReference,
              fullName: this.userDetails.name,
            },
          },
        );
        this.$emit('refresh');
      } catch (e) {
        notifyErrorWithDetails(e.message, e.details?.fullMessage);
      }
    },

    onRequestCancellation() {
      this.openComponentDialog(
        CancelAppointmentDialog,
        {
          cancellationTitle: this.$t('appointments.cancellationRequest.cancellationRequestTitle'),
        },
        async ({ cancellationReason, cancellationType, cancellationReasonLabel }) => {
          await this.requestCancellation(cancellationReason, cancellationType, cancellationReasonLabel);
        },
      );
    },

    onAddPastStaffActivity() {
      if (this.isUpdating) {
        return;
      }
      const {
        user,
        userName,
        clinic,
        id: apptId,
        scheduledStartTimestamp,
        duration = 3600,
        sessionNoteTemplateId = null,
        activityType,
      } = this.appointment;

      const appointmentData = [
        'integrationReference',
        'integrationClientParRef',
        'integrationStaffParRef',
        'integrationClientRef',
        'integrationStaffRef',
        'integrationAppointmentType',
        'integration',
        'integrationExternalUrl',
      ].reduce((obj, key) => {
        if (this.appointment[key] !== undefined) {
          obj[key] = this.appointment[key];
        }
        return obj;
      }, {});

      const endTimestamp = Timestamp.fromMillis(scheduledStartTimestamp.toMillis() + duration * 1000);

      const { session, supervision, form } = SESSION_ACTIVITY_TYPES;
      const excludedOptions = { session, supervision, form };

      const sessionActivity = {
        startTimestamp: scheduledStartTimestamp,
        endTimestamp,
        status: SESSION_ACTIVITY_STATUSES.FINISHED,
        type: activityType || '',
        user,
        userName,
        client: null,
        clinic,
        notes: '',
        sessionDurationOffset: 0,
        sessionNoteTemplateId,
        appointment: apptId,
        appointmentData,
        session: null,
        sessionName: '',
        createdBy: this.userDetails.userId,
        version: 2,
        isDeleted: false,
      };

      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity,
        title: this.$t('activities.buttonAddSessionActivity'),
        excludedOptions,
        lockDate: true,
        linkedAppointment: this.appointment,
        saveBtnLabel: this.$t('common.buttonSaveAndLink'),
      }, async (newSessionActivity) => {
        const sessionActivitiesDb = new SessionActivitiesDB();

        try {
          this.isUpdating = true;
          const createdSessionActivity = await sessionActivitiesDb.create({
            ...sessionActivity,
            ...newSessionActivity,
          });

          if (createdSessionActivity.id) {
            const appointmentsDb = new AppointmentsDB();
            await appointmentsDb.set({
              sessionActivity: createdSessionActivity.id,
              status: SESSION_ACTIVITY_STATUSES.COMPLETED,
            }, apptId);

            sessionAnalytics.trackActivityCreatedFromPastAppointment({
              sessionActivityId: createdSessionActivity.id,
              sessionActivityType: createdSessionActivity.type,
              appointmentId: apptId,
            });

            this.$emit('refresh');

            this.openComponentDialog(
              SessionActivityDialog,
              { sessionActivityId: createdSessionActivity.id },
              async () => {},
            );
          }
        } catch (err) {
          this.notifyNegative(err.message);
          logEvent(err, {
            context: {
              context_data: {
                appointmentId: this.appointment.id,
              },
            },
          });
        } finally {
          this.isUpdating = false;
        }
      });
    },

  },
};
</script>
<style lang="scss" scoped>
.card-body {
  @media (max-width: $breakpoint-sm-max) {
    width: 100%;
    white-space: break-spaces;
    max-height: 80px;
    overflow-y: auto;
  }

}

.calendar-day {
  border-right: solid $grey-2 1px;
}

.activity-type-icon {
  position: relative;
  vertical-align: middle;
  display: inline-block;
  height: 1em;
  width: 1em;
}
</style>
